// Calculate quantity metrics similar to the tableimport { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  formatCurrency,
  formatDistance,
  formatBookingDuration,
} from "@/lib/Formatters/formatCurrency";
import dayjs from "dayjs";
import { ExclamationTriangleIcon, StarIcon } from "@heroicons/react/20/solid";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import {
  MapPin,
  Clock,
  Car,
  Receipt,
  Info,
  DollarSign,
  Fuel,
  Ship,
  PencilIcon,
} from "lucide-react";
import { BookingEditReservationDetailsDialog } from "@/app/Bookings/Components/Dialogs/BookingEditReservationDetailsDialog";
import { formatDate } from "@/lib/Formatters/formatDate";
import { BookingRecord } from "@/app/Bookings/GraphQL/bookingRecordQuery";
import { Button } from "@/components/catalyst/button";
import { RawText } from "@/lib/Components/RawText";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { RelocationInclusionType, RelocationType } from "@/gql/graphql";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { TripMap } from "@/app/Cities/Trips/Components/TripMap";
import { useTranslations } from "use-intl";

export function BookingTripCard({ booking }: { booking: BookingRecord }) {
  const t = useTranslations("booking.cards.tripCard");
  const tRelocation = useTranslations("relocation");
  const { open } = useDialog(BookingEditReservationDetailsDialog);
  // Map inclusion types to their respective icons
  const getInclusionIcon = (type: RelocationInclusionType) => {
    switch (type) {
      case RelocationInclusionType.Charge:
        return <DollarSign className="size-4 text-blue-600" />;
      case RelocationInclusionType.Fuel:
        return <Fuel className="size-4 text-green-600" />;
      case RelocationInclusionType.Ferry:
        return <Ship className="size-4 text-indigo-600" />;
      case RelocationInclusionType.Negative:
        return <ExclamationTriangleIcon className="size-4 text-red-600" />;
      case RelocationInclusionType.Other:
      default:
        return <StarIcon className="size-4 text-amber-600" />;
    }
  };

  const booked = booking.relocation.bookedBookingsCount ?? 0;
  const total = booking.relocation.count + booked;
  const percent = total > 0 ? (booked / total) * 100 : 0;

  const QuantityDisplay = () => {
    if (total === 0) {
      return (
        <div className="text-center">
          <span className="text-xs text-gray-400">{t("noneAvailable")}</span>
        </div>
      );
    }

    return (
      <div className="space-y-1">
        <div className="flex items-center justify-between gap-1 text-xs">
          <span className="font-medium text-gray-900">
            {t("booked", { count: booked })}
          </span>
          <span className="text-gray-400">/</span>
          <span className="text-gray-500">{t("total", { count: total })}</span>
        </div>
        <Progress
          value={percent}
          className="h-1.5 w-full rounded-full bg-gray-200"
          indicatorClassName={cn(
            "rounded-full transition-all duration-300",
            percent < 50
              ? "bg-yellow-400"
              : percent < 80
                ? "bg-orange-400"
                : "bg-green-500",
          )}
        />
      </div>
    );
  };

  return (
    <Card className="overflow-hidden border-2 border-gray-100">
      <div className="relative">
        <TripMap
          cityAName={booking.departureOffice.city.name}
          cityBName={booking.deliveryOffice.city.name}
          cityALat={booking.departureOffice.city.lat}
          cityALng={booking.departureOffice.city.lng}
          cityBLat={booking.deliveryOffice.city.lat}
          cityBLng={booking.deliveryOffice.city.lng}
          zoomControl={false}
        />
        <div className="absolute right-3 top-3 z-[1000]">
          <Button
            color="white"
            aria-label={t("editReservation")}
            onClick={() => {
              open({
                bookingId: booking.id,
              });
            }}
          >
            <PencilIcon data-slot="icon" />
          </Button>
        </div>

        <div className="absolute left-3 top-3 z-[1000] min-w-[100px] rounded-lg bg-white/90 p-2 shadow-sm backdrop-blur-sm">
          <QuantityDisplay />
        </div>
      </div>

      <CardContent className="text-sm">
        <div className="mt-6 grid grid-cols-2 gap-8">
          {/* Departure Location */}
          <div className="relative border-l-4 border-blue-200 pl-6">
            <div className="absolute -left-[10px] -top-[3px] rounded-full bg-white p-1">
              <div className="flex size-5 items-center justify-center rounded-full bg-blue-500 text-xs font-bold text-white">
                A
              </div>
            </div>
            <div className="font-semibold">
              {booking.departureOffice.name}
              {booking.departureOffice.code
                ? ` (${booking.departureOffice.code})`
                : ""}
            </div>

            <div className="mt-1 flex items-center font-bold text-blue-600">
              <span>
                {dayjs(booking.depart_at).format("ddd, MMM D • h:mm A")}
              </span>
            </div>

            <address className="mt-3 grid gap-0.5 text-xs not-italic text-gray-500">
              <p>{booking.departureOffice.address.formatted_address_line_1}</p>
              <p>{booking.departureOffice.address.formatted_address_line_2}</p>
            </address>
          </div>

          <div className="relative border-l-4 border-green-200 pl-6">
            <div className="absolute -left-[10px] -top-[3px] rounded-full bg-white p-1">
              <div className="flex size-5 items-center justify-center rounded-full bg-green-500 text-xs font-bold text-white">
                B
              </div>
            </div>
            <div className="font-semibold">
              {booking.deliveryOffice.name}{" "}
              {booking.deliveryOffice.code
                ? ` (${booking.deliveryOffice.code})`
                : ""}
            </div>

            <div className="mt-1 flex items-center font-bold text-green-600">
              <span>
                {dayjs(booking.deliver_at).format("ddd, MMM D • h:mm A")}
              </span>
            </div>

            <address className="mt-3 grid gap-0.5 text-xs not-italic text-gray-500">
              <p>{booking.deliveryOffice.address.formatted_address_line_1}</p>
              <p>{booking.deliveryOffice.address.formatted_address_line_2}</p>
            </address>
          </div>
        </div>

        <Separator className="my-6" />

        {/* Trip Details Section */}
        <div className="grid gap-5">
          <div className="grid grid-cols-3 gap-4">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                <Clock className="h-3.5 w-3.5" /> {t("duration")}
              </div>
              <div className="font-medium">
                {formatBookingDuration(
                  booking.discounted_units,
                  booking.full_price_units,
                  booking.hire_unit_type,
                  booking.relocation.type === RelocationType.GapRental,
                )}
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                <MapPin className="h-3.5 w-3.5" /> {t("mileage")}
              </div>
              <div className="font-medium">
                {formatDistance(booking.distance_allowed, booking.measurement)}
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                <Car className="h-3.5 w-3.5" /> {t("vehicle")}
              </div>
              <div className="font-medium">
                {booking.vehicle.code
                  ? `${booking.vehicle.code}`
                  : booking.vehicle.name}
              </div>
            </div>
          </div>
        </div>

        {booking.inclusions.length > 0 && (
          <>
            <Separator className="my-6" />
            <div className="grid gap-3">
              <div className="flex items-center gap-1.5 font-medium text-gray-800">
                <Receipt className="h-4 w-4" /> {t("inclusionsTitle")}
              </div>
              <div className="rounded-lg bg-gray-50 p-4">
                <ul className="grid gap-3">
                  {booking.inclusions.map((inclusion) => (
                    <li className="flex items-start gap-2" key={inclusion.id}>
                      {getInclusionIcon(
                        inclusion.type as RelocationInclusionType,
                      )}
                      <div className="flex-1">
                        <span className="font-medium">
                          {tRelocation(`inclusions.type.${inclusion.type}`)}
                        </span>
                        {inclusion.has_free_tank ? (
                          <div className="mt-1 text-xs font-semibold text-green-600">
                            {t("oneFreeTank")}
                          </div>
                        ) : inclusion.description ? (
                          <div className="mt-1 text-xs text-gray-500">
                            <RawText>{inclusion.description}</RawText>
                          </div>
                        ) : null}
                      </div>
                      {!inclusion.has_free_tank && inclusion.value ? (
                        <div className="font-medium text-gray-900">
                          {formatCurrency(inclusion.value, booking.currency)}
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </>
        )}

        {(booking.supplier_reference ||
          booking.preferred_ferry_date ||
          booking.customer_note) && (
          <>
            <Separator className="my-6" />
            <div className="grid gap-3">
              <div className="flex items-center gap-1.5 font-medium text-gray-800">
                <Info className="h-4 w-4" /> {t("additionalNotesTitle")}
              </div>
              <div className="rounded-lg bg-gray-50 p-4">
                <dl className="grid gap-4">
                  {booking.supplier_reference && (
                    <div className="flex justify-between gap-2">
                      <dt className="text-gray-500">
                        {t("supplierReference", {
                          supplier: booking.supplier.name,
                        })}
                      </dt>
                      <dd className="text-right font-medium">
                        {booking.supplier_reference}
                      </dd>
                    </div>
                  )}

                  {booking.preferred_ferry_date && (
                    <div className="flex justify-between gap-2">
                      <dt className="text-gray-500">
                        {t("preferredFerryDate")}
                      </dt>
                      <dd className="text-right font-medium">
                        {formatDate(booking.preferred_ferry_date)}
                      </dd>
                    </div>
                  )}

                  {booking.customer_note && (
                    <div className="flex flex-col space-y-2">
                      <dt className="text-gray-500">{t("customerNote")}</dt>
                      <dd className="rounded-md bg-white p-3 text-gray-800">
                        <RawText>{booking.customer_note}</RawText>
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
            </div>
          </>
        )}
      </CardContent>

      {booking.relocationLine?.reference ? (
        <CardFooter className="flex flex-row items-center justify-end border-t bg-gray-50 px-6 py-3">
          <div className="text-xs text-gray-500">
            {t("lineReference", {
              reference: booking.relocationLine.reference,
            })}
          </div>
        </CardFooter>
      ) : null}
    </Card>
  );
}
